import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { toHex } from 'color2k';
import { range, extension, command, helper, MarkExtension, ExtensionTag, isElementDomNode, ExtensionPriority, isString, omitExtraAttributes, joinStyles } from '@remirror/core';
import { ExtensionTextColorMessages } from '@remirror/messages';
import { getThemeVar } from '@remirror/theme';

var setTextColorOptions = {
  icon: 'fontColor',
  description: _ref => {
    var t = _ref.t;
    return t(ExtensionTextColorMessages.SET_COLOR_DESCRIPTION);
  },
  label: _ref2 => {
    var t = _ref2.t;
    return t(ExtensionTextColorMessages.SET_COLOR_LABEL);
  }
};
var hueRange = range([10]);

function createHuePalette(props) {
  var t = props.t,
      name = props.name,
      labelDescriptor = props.labelDescriptor,
      hueDescriptor = props.hueDescriptor;
  var label = t(labelDescriptor);
  var hues = hueRange.map(hue => ({
    label: t(hueDescriptor, {
      hue
    }),
    color: getThemeVar('hue', name, hue)
  }));
  return {
    label,
    hues
  };
}
/**
 * The default color palette which uses css properties to update the colors.
 */


function palette(t) {
  var black = {
    label: t(ExtensionTextColorMessages.BLACK),
    color: '#000'
  };
  var white = {
    label: t(ExtensionTextColorMessages.WHITE),
    color: '#fff'
  };
  var transparent = {
    label: t(ExtensionTextColorMessages.TRANSPARENT),
    color: 'transparent'
  };
  var hues = {
    gray: createHuePalette({
      t,
      name: 'gray',
      labelDescriptor: ExtensionTextColorMessages.GRAY,
      hueDescriptor: ExtensionTextColorMessages.GRAY_HUE
    }),
    blue: createHuePalette({
      t,
      name: 'blue',
      labelDescriptor: ExtensionTextColorMessages.BLUE,
      hueDescriptor: ExtensionTextColorMessages.BLUE_HUE
    }),
    cyan: createHuePalette({
      t,
      name: 'cyan',
      labelDescriptor: ExtensionTextColorMessages.CYAN,
      hueDescriptor: ExtensionTextColorMessages.CYAN_HUE
    }),
    grape: createHuePalette({
      t,
      name: 'grape',
      labelDescriptor: ExtensionTextColorMessages.GRAPE,
      hueDescriptor: ExtensionTextColorMessages.GRAPE_HUE
    }),
    green: createHuePalette({
      t,
      name: 'green',
      labelDescriptor: ExtensionTextColorMessages.GREEN,
      hueDescriptor: ExtensionTextColorMessages.GREEN_HUE
    }),
    indigo: createHuePalette({
      t,
      name: 'indigo',
      labelDescriptor: ExtensionTextColorMessages.INDIGO,
      hueDescriptor: ExtensionTextColorMessages.INDIGO_HUE
    }),
    lime: createHuePalette({
      t,
      name: 'lime',
      labelDescriptor: ExtensionTextColorMessages.LIME,
      hueDescriptor: ExtensionTextColorMessages.LIME_HUE
    }),
    orange: createHuePalette({
      t,
      name: 'orange',
      labelDescriptor: ExtensionTextColorMessages.ORANGE,
      hueDescriptor: ExtensionTextColorMessages.ORANGE_HUE
    }),
    pink: createHuePalette({
      t,
      name: 'pink',
      labelDescriptor: ExtensionTextColorMessages.PINK,
      hueDescriptor: ExtensionTextColorMessages.PINK_HUE
    }),
    red: createHuePalette({
      t,
      name: 'red',
      labelDescriptor: ExtensionTextColorMessages.RED,
      hueDescriptor: ExtensionTextColorMessages.RED_HUE
    }),
    teal: createHuePalette({
      t,
      name: 'teal',
      labelDescriptor: ExtensionTextColorMessages.TEAL,
      hueDescriptor: ExtensionTextColorMessages.TEAL_HUE
    }),
    violet: createHuePalette({
      t,
      name: 'violet',
      labelDescriptor: ExtensionTextColorMessages.VIOLET,
      hueDescriptor: ExtensionTextColorMessages.VIOLET_HUE
    }),
    yellow: createHuePalette({
      t,
      name: 'yellow',
      labelDescriptor: ExtensionTextColorMessages.YELLOW,
      hueDescriptor: ExtensionTextColorMessages.YELLOW_HUE
    })
  };
  return {
    black,
    hues,
    transparent,
    white
  };
}
var CSS_VAR_REGEX = /^var\((--[A-Za-z-]+)\);?/;
var TEXT_COLOR_ATTRIBUTE = 'data-text-color-mark';

var _excluded = ["color"];

var _dec, _dec2, _dec3, _dec4, _class, _class2;
/**
 * Wraps text with a styled span using the color css property. The name of the wrapper tag should be configurable.
 */

var TextColorExtension = (_dec = extension({
  defaultOptions: {
    defaultColor: '',
    palette
  },
  staticKeys: ['defaultColor']
}), _dec2 = command(setTextColorOptions), _dec3 = command(), _dec4 = helper(), _dec(_class = (_class2 = class TextColorExtension extends MarkExtension {
  get name() {
    return 'textColor';
  }

  createTags() {
    return [ExtensionTag.FormattingMark, ExtensionTag.FontStyle];
  }

  createMarkSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({}, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        color: {
          default: this.options.defaultColor
        }
      }),
      parseDOM: [{
        tag: "span[".concat(TEXT_COLOR_ATTRIBUTE, "]"),
        getAttrs: dom => {
          if (!isElementDomNode(dom)) {
            return;
          }

          var color = dom.getAttribute(TEXT_COLOR_ATTRIBUTE);

          if (!color) {
            return;
          }

          return _objectSpread(_objectSpread({}, extra.parse(dom)), {}, {
            color
          });
        }
      }, {
        // Get the color from the css style property. This is useful for pasted content.
        style: 'color',
        priority: ExtensionPriority.Low,
        getAttrs: color => {
          if (!isString(color)) {
            return;
          }

          return {
            color
          };
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: mark => {
        var _omitExtraAttributes = omitExtraAttributes(mark.attrs, extra),
            color = _omitExtraAttributes.color,
            other = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var extraAttrs = extra.dom(mark);
        var style = extraAttrs.style;

        if (color) {
          style = joinStyles({
            color
          }, style);
        }

        return ['span', _objectSpread(_objectSpread(_objectSpread({}, other), extraAttrs), {}, {
          style,
          [TEXT_COLOR_ATTRIBUTE]: color
        }), 0];
      }
    });
  }
  /**
   * Set the text color value for the selected text.
   *
   * To remove the color you can set the value to null.
   */


  setTextColor(color, options) {
    return this.store.commands.applyMark.original(this.type, {
      color
    }, options === null || options === void 0 ? void 0 : options.selection);
  }
  /**
   * Remove the color mark from the selection.
   */


  removeTextColor(options) {
    return this.store.commands.removeMark.original(_objectSpread(_objectSpread({
      type: this.type
    }, options), {}, {
      expand: true
    }));
  }
  /**
   * Get the color from the provided string. The string can be a computed
   * property as well.
   */


  getHexColor(color) {
    var match = color.match(CSS_VAR_REGEX);
    var cssVar = match === null || match === void 0 ? void 0 : match[1];

    if (cssVar) {
      color = getComputedStyle(this.store.view.dom).getPropertyValue(cssVar);
    }

    return toHex(color);
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "setTextColor", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "setTextColor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "removeTextColor", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "removeTextColor"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getHexColor", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "getHexColor"), _class2.prototype)), _class2)) || _class);

export { CSS_VAR_REGEX, TEXT_COLOR_ATTRIBUTE, TextColorExtension, palette, setTextColorOptions };
